/**
 * useLogin — TanStack Query mutation that wraps POST /auth/login.
 *
 * On success: stores token + user in authStore (which also fires
 * ``i18n.changeLanguage(user.preferred_lang)`` for I18N-02), then redirects:
 *   - If ``user.password_must_rotate`` → ``/rotate-password`` (USR-04 client-side gate)
 *   - Else → the ``next`` query-string param (AUTH-05) or ``"/"`` as default
 *     ("/" is then routed to /positions by App.tsx — role-agnostic landing per BUG-061).
 *
 * On error: surfaces a sonner toast with ``formatValidationError(err)`` (BUG-010).
 */
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import { apiFetch, formatValidationError } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import type { Role, TokenResponse } from "@/types/api";

export interface LoginCredentials {
  username: string;
  password: string;
}

// BUG-039: only accept ``next`` values that are unambiguously relative paths
// inside the SPA. Reject protocol-relative URLs (``//evil.com``) and any value
// that doesn't start with a single ``/`` (e.g. ``https://evil.com``,
// ``javascript:...``). Falls back to the default safe path.
// BUG-061: default landing is "/" which App.tsx routes to /positions (role-agnostic).
// Previously "/admin/users" which 403'd every non-Content-Admin role.
const SAFE_DEFAULT = "/";

// BUG-061 sibling (promoted from Plan 02-10 deferred → fix-now 2026-05-22):
// Routes gated to specific roles via RequireRole / backend role-allowlist.
// safeNext rejects post-login ``next`` targets the user can't access, otherwise
// hr_recruiter / hr_manager / hiring_manager get redirected to /admin/users
// (a stale `?next=/admin/users` URL leaks via ProtectedRoute) → 403.
// Keep aligned with App.tsx route role boundaries.
const ROLE_GATED_ROUTES: Record<string, Role[]> = {
  "/admin/users": ["content_admin"],
  "/admin/audit": ["content_admin", "super_admin"],
  "/templates": ["content_admin"],
  "/templates/new": ["content_admin"],
  "/question-bank": ["content_admin"],
};

function safeNext(raw: string | null, userRole?: Role): string {
  if (!raw) return SAFE_DEFAULT;
  if (!raw.startsWith("/")) return SAFE_DEFAULT;
  if (raw.startsWith("//")) return SAFE_DEFAULT;
  // BUG-061 sibling: check user-role can access the target route. Use
  // longest-prefix match — `/templates/new` matches exactly; `/templates/123/edit`
  // matches `/templates` as a prefix gate. Strip query/hash before comparing.
  if (userRole) {
    const pathOnly = raw.split("?")[0].split("#")[0];
    for (const gatedPath of Object.keys(ROLE_GATED_ROUTES)) {
      if (pathOnly === gatedPath || pathOnly.startsWith(gatedPath + "/")) {
        if (!ROLE_GATED_ROUTES[gatedPath].includes(userRole)) {
          return SAFE_DEFAULT;
        }
      }
    }
  }
  return raw;
}

export function useLogin() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next");

  return useMutation({
    mutationFn: (creds: LoginCredentials) =>
      apiFetch<TokenResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify(creds),
      }),
    onSuccess: (data) => {
      login(data.access_token, data.user);
      if (data.user.password_must_rotate) {
        navigate("/rotate-password", { replace: true });
      } else {
        // BUG-061 sibling: pass user.role so safeNext can reject role-gated
        // targets (e.g. hr_recruiter logging in via /login?next=/admin/users).
        navigate(safeNext(rawNext, data.user.role), { replace: true });
      }
    },
    onError: (err) => {
      toast.error(formatValidationError(err));
    },
  });
}
